import { useState } from 'react';
import { toast } from 'sonner';
import { MIN_CUSTOM_SANDBOX_DISK_GB } from '@runloop/reflex-contract';
import { type ServiceSettings } from '@reflex/shared';
import { Input } from '@reflex/ui/components/ui/input';
import { Label } from '@reflex/ui/components/ui/label';
import { Button } from '@reflex/ui/components/ui/button';
import { SettingsRow } from '@reflex/ui/components/shared/SettingsRow';
import { PageDescription } from '@reflex/ui/components/layout/Typography';
import { AdminSubPageLayout } from '@reflex/ui/components/layout/AdminSubPageLayout';
import { AsyncContent } from '@/components/shared/AsyncContent';
import {
  useServiceSettings,
  useUpdateServiceSettings,
} from '@/hooks/config/useServiceSettingsAdmin';

const DESCRIPTION =
  'Control deployment-wide Reflex limits. These settings apply to every organization.';

export function AdminServiceSettingsPage() {
  const settingsQuery = useServiceSettings();

  return (
    <AdminSubPageLayout title="Service settings" description={DESCRIPTION}>
      <AsyncContent
        isLoading={settingsQuery.isLoading}
        isError={settingsQuery.isError}
        loadingMessage="Loading service settings…"
        errorMessage="Failed to load service settings."
      >
        {settingsQuery.data && (
          <ServiceSettingsForm
            key={settingsQuery.data.maxSandboxDiskSizeGb}
            initial={settingsQuery.data}
          />
        )}
      </AsyncContent>
    </AdminSubPageLayout>
  );
}

function ServiceSettingsForm({ initial }: { initial: ServiceSettings }) {
  const updateSettings = useUpdateServiceSettings();
  const [maxDiskSize, setMaxDiskSize] = useState(String(initial.maxSandboxDiskSizeGb));
  const parsedMaxDiskSize = Number(maxDiskSize);
  const valid =
    Number.isInteger(parsedMaxDiskSize) && parsedMaxDiskSize >= MIN_CUSTOM_SANDBOX_DISK_GB;
  const dirty = maxDiskSize !== String(initial.maxSandboxDiskSizeGb);

  async function handleSave() {
    if (!dirty || !valid) return;
    await updateSettings.mutateAsync(
      { maxSandboxDiskSizeGb: parsedMaxDiskSize },
      {
        onSuccess: () => toast.success('Service settings saved'),
        onError: (error) =>
          toast.error('Failed to save service settings', { description: error.message }),
      },
    );
  }

  return (
    <div className="space-y-6">
      <SettingsRow
        title="Maximum sandbox disk size"
        description="Largest disk an organization can set for a custom sandbox size."
        control={
          <div className="flex items-center gap-2">
            <Label htmlFor="max-sandbox-disk-size" className="sr-only">
              Maximum sandbox disk size
            </Label>
            <Input
              id="max-sandbox-disk-size"
              className="w-28"
              type="number"
              min={MIN_CUSTOM_SANDBOX_DISK_GB}
              step={1}
              value={maxDiskSize}
              aria-invalid={!valid}
              onChange={(event) => setMaxDiskSize(event.target.value)}
            />
            <span className="text-sm text-muted-foreground">GB</span>
          </div>
        }
      />

      {!valid && (
        <PageDescription className="mt-0 text-xs text-status-error">
          Enter a whole number of at least {MIN_CUSTOM_SANDBOX_DISK_GB} GB.
        </PageDescription>
      )}

      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          disabled={!dirty || updateSettings.isPending}
          onClick={() => setMaxDiskSize(String(initial.maxSandboxDiskSizeGb))}
        >
          Discard changes
        </Button>
        <Button
          type="button"
          disabled={!dirty || !valid || updateSettings.isPending}
          onClick={handleSave}
        >
          {updateSettings.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </div>
  );
}
